import React, { useState, useMemo, useCallback } from 'react';
import { TableSortBy } from '@veneer/core';

import { DeviceSharingRequest } from 'src/types/devices';
import { getExpiresAt } from 'src/utils/format-access-remaining';

export type UseSharedTableSortingReturn = {
  sortedDevices: DeviceSharingRequest[];
  sortBy: TableSortBy;
  handleSort: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    sort: TableSortBy
  ) => void;
  freezeAtOrder: (ids: string[]) => void;
};

const DEFAULT_SORT_BY: TableSortBy = {
  id: 'accessRemaining',
  type: 'ascending'
};

export function useSharedTableSorting(
  devices: DeviceSharingRequest[] | null,
  noDeviceName: string
): UseSharedTableSortingReturn {
  const [sortBy, setSortBy] = useState<TableSortBy>(DEFAULT_SORT_BY);
  const [frozenOrder, setFrozenOrder] = useState<string[] | null>(null);

  const handleSort = useCallback(
    (
      _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      sort: TableSortBy
    ) => {
      setFrozenOrder(null);
      setSortBy(sort);
    },
    []
  );

  const freezeAtOrder = useCallback((ids: string[]) => {
    setFrozenOrder(ids);
  }, []);

  const sortedDevices = useMemo(() => {
    if (!devices) return [];

    if (frozenOrder !== null) {
      return frozenOrder
        .map((id) => devices.find((d) => d.id === id))
        .filter((d): d is DeviceSharingRequest => d !== undefined);
    }

    if (!sortBy) return devices;

    const direction = sortBy.type === 'ascending' ? 1 : -1;

    return [...devices].sort((a, b) => {
      switch (sortBy.id) {
        case 'deviceName':
          return (
            direction *
            (a.device.device_family ?? noDeviceName)
              .toLowerCase()
              .localeCompare(
                (b.device.device_family ?? noDeviceName).toLowerCase()
              )
          );
        case 'serialNumber':
          return (
            direction *
            a.device.device_serial_nr
              .toLowerCase()
              .localeCompare(b.device.device_serial_nr.toLowerCase())
          );
        case 'thirdPartyEmail':
          return (
            direction *
            a.user.email.toLowerCase().localeCompare(b.user.email.toLowerCase())
          );
        case 'accessRemaining': {
          const aExpiry = getExpiresAt(a.policies);
          const bExpiry = getExpiresAt(b.policies);
          const aMs = aExpiry ? new Date(aExpiry).getTime() : Infinity;
          const bMs = bExpiry ? new Date(bExpiry).getTime() : Infinity;
          if (aMs === bMs) return 0;
          return direction * (aMs - bMs);
        }
        default:
          return 0;
      }
    });
  }, [devices, sortBy, frozenOrder, noDeviceName]);

  return {
    sortedDevices,
    sortBy,
    handleSort,
    freezeAtOrder
  };
}
